import { Injectable } from '@angular/core';

// RxJs
import { delay, distinctUntilChanged, filter, finalize, of, Subject, switchMap, tap } from 'rxjs';
import { map, mergeMap, catchError, concatMap, takeWhile, takeUntil, take } from 'rxjs/operators';

// NgRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  DepreciationFourthStepEffectsActions,
  DepreciationEffectsActions,
  DepreciationSecondStepEffectsActions,
  DepreciationThirdStepEffectsActions,
  DepreciationFirstStepEffectsActions,
  DepreciationFifthStepEffectsActions,
  DepreciationStepperActions,
  DepreciationSkipEventsStepEffectsActions
} from '../actions/depreciation.actions';

// Services
import { DepreciationService } from '@core/providers/services/depreciation/depreciation.service';
import { SocketService } from '@core/providers/services/socket/socket.service';

// Utils
import { FormatBase64, looperBase64 } from '@core/utils/functions/looperBase64.util';

// Models
import {
  Depreciation,
  DepreciationStatus,
  FirstStepStatus,
  ThirdStepStatus
} from '@core/models/depreciation.interface';
import { environment as ENV } from '@environments/environment';
import { selectDepreciationIDState, selectDepreciationState } from '@core/store/selectors/depreciation.selectors';
import { Store } from '@ngrx/store';
import { AppState } from '@core/store/reducers';



@Injectable()
export class DepreciationEffects {

  private errorMsg: string;
  private errorSocket: { rowsWithProblems: null, errorMessage: string };

  // Store
  getStore$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationEffectsActions.getStore),
    mergeMap(() => this.depreciationService.getStore().pipe(
      map(({ depreciation, step, status }) => {
        if (step <= 1) {
          switch (status) {
            case 'FINISH':
              (depreciation as Depreciation).firstStep.status = status;
              (depreciation as Depreciation).currentStep = 1;
              break;
            case 'FINISH_ERROR':
              (depreciation as Depreciation).firstStep.error = status;
              (depreciation as Depreciation).firstStep.status = FirstStepStatus.ERROR;
              break;
          }
        }

        return DepreciationEffectsActions.injectStore({ depreciation });
      }),
      catchError(() => of(DepreciationEffectsActions.injectError({ error: this.errorMsg })))
    )))
  );

  // First step
  getStoreInsteandOfInitLostEvents$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFirstStepEffectsActions.getStoreInsteandOfInitLostEvents),
    mergeMap(() => this.depreciationService.getStoreInsteandOfInitLostEvents().pipe(
      tap(() => this.store.dispatch(DepreciationFirstStepEffectsActions.getHistory())),
      map(status => {
        console.log({ getStoreInsteandOfInitLostEvents$witch: status });
        switch (status) {
          case 'FINISH': return DepreciationStepperActions.next(); break;
          case 'FINISH_ERROR': return DepreciationFirstStepEffectsActions.injectError({ error: status }); break;
          default: return DepreciationFirstStepEffectsActions.injectStoreInsteandOfInitLostEvents({ currentStatus: status }); break;
        }
      }),
      catchError(() => of(DepreciationFirstStepEffectsActions.injectError({ error: this.errorMsg })))
    )))
  );


  getHistory$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFirstStepEffectsActions.getHistory),
    mergeMap(() => this.depreciationService.getHistory().pipe(
      map(history => DepreciationFirstStepEffectsActions.injectHistory({ history })),
      catchError(({ error }) => of(DepreciationFirstStepEffectsActions.injectError({ error })))
    )))
  );


  initLostEvents$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFirstStepEffectsActions.initLostEvents),
    mergeMap(({ period }) => this.depreciationService.initLostEvents(period).pipe(
      map((msg) => {
        console.log({ initLostEvents: msg });
        return msg === 'ERROR' ? DepreciationFirstStepEffectsActions.injectError({ error: this.errorMsg })
                               : DepreciationFirstStepEffectsActions.connectLostEventsLoadSocket();
      }),
      catchError(() => of(DepreciationFirstStepEffectsActions.injectError({ error: this.errorMsg })))
    )))
  );


  connectLostEventsLoadSocket$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFirstStepEffectsActions.connectLostEventsLoadSocket),
    tap(() => this.disconnectSocket$ = new Subject()),
    switchMap(() => this.store.select(selectDepreciationIDState).pipe(
      filter(id => !!id),
      take(1)
    )),
    mergeMap(id => this.socketService.openConnection(
      `${ ENV.DEPRECIATION.STEPPER.FIRST_STEP.WSS.LOST_EVENTS_LOAD }${ id }`,
      { id }, { status: 'IN_PROGRESS', percentage: 0 }
    ).pipe(
      map(socket => DepreciationFirstStepEffectsActions.injectLostEventsLoadSocket({ socket })),
      takeUntil(this.disconnectSocket$),
      tap(({ socket: { status }}) => status === 'FINISH' && this.socketService.closeConnection().subscribe()),
      finalize(() => console.log('finalize LostEventsLoadSocket')),
      catchError(err => of(DepreciationFirstStepEffectsActions.injectError({ error: this.errorSocket })))
    ))
  ));


  disconnectLostEventsLoadSocket$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFirstStepEffectsActions.disconnectLostEventsLoadSocket),
    mergeMap(({ isCancelled }) => this.socketService.closeConnection().pipe(
      map((isClosed) => {
        this.disconnectSocket$.next(isClosed);
        this.disconnectSocket$.complete();
        return isCancelled ? DepreciationFirstStepEffectsActions.cancelLostEventsLoadSocket()
                           : DepreciationStepperActions.next();
      })
    ))
  ));

  // Second step
  uploadUsefulLife$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationSecondStepEffectsActions.sendFile),
    mergeMap(({ file }) => this.depreciationService.uploadUsefulLife(file as File).pipe(
      map(() => DepreciationSecondStepEffectsActions.injectFile({ file })),
      catchError(({ error }) => of(DepreciationSecondStepEffectsActions.injectError({ error })))
    )))
  );

  // Third step
  uploadCaf$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationThirdStepEffectsActions.sendFile),
    mergeMap(({ file }) => this.depreciationService.uploadCaf(file!).pipe(
      tap(() => this.store.dispatch(DepreciationThirdStepEffectsActions.injectFile({ file }))),
      map(() => DepreciationThirdStepEffectsActions.connectCafLoadSocket()),
      catchError(({ error }) => of(DepreciationThirdStepEffectsActions.injectError({ error })))
    )))
  );


  connectCafLoadSocket$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationThirdStepEffectsActions.connectCafLoadSocket),
    tap(() => this.disconnectSocket$ = new Subject()),
    switchMap(() => this.store.select(selectDepreciationIDState)),
    filter(id => !!id),
    mergeMap(id => this.socketService.openConnection(
      `${ ENV.DEPRECIATION.STEPPER.THIRD_STEP.WSS.CAF_LOAD }${ id }`,
      { id }, { status: 'IN_PROGRESS', percentage: 0 }
    ).pipe(
      map(socket =>  DepreciationThirdStepEffectsActions.injectCafLoadSocket({ socket })),
      takeUntil(this.disconnectSocket$),
      tap(({ socket: { status }}) => status === 'FINISH' && this.socketService.closeConnection().subscribe()),
      finalize(() => console.log('finalize connectCafLoadSocket')),
      catchError(err => {
        console.error({ disconnectSocket$Err: err });
        return of(DepreciationThirdStepEffectsActions.injectError({ error: this.errorSocket }));
      }),
    )),
  ));


  disconnectCafLoadSocket$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationThirdStepEffectsActions.disconnectCafLoadSocket),
    mergeMap(({ isCancelled }) => this.socketService.closeConnection().pipe(
      map((isClosed) => {
        this.disconnectSocket$.next(isClosed);
        this.disconnectSocket$.complete();
        return isCancelled ? DepreciationThirdStepEffectsActions.cancelCafLoadSocket()
                           : DepreciationStepperActions.next();
      })
    ))
  ));


  // Skip events step
  getSkipEvents$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationSkipEventsStepEffectsActions.getSkipEvents),
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
    mergeMap(({ skip, eventType, filter, search, page, limit }) => this.depreciationService.getSkipEvents(
      skip, eventType, filter, search, page, limit
    ).pipe(
      map(({ tools, length }) => {
        console.log({ trim: search.trim().length > 0, length });
        if (search.trim().length > 0 && 0 === length) {
          return DepreciationSkipEventsStepEffectsActions.injectError(
            { error: { message: 'No hay eventos con esa búsqueda' }}
          );
        } else {
          return DepreciationSkipEventsStepEffectsActions.injectSkipEvents({
            events: { tools, length }, params: { skip, eventType, filter, search, page, limit }
          });
        }
      }),
      catchError(({ error }) => of(DepreciationSkipEventsStepEffectsActions.injectError({ error })))
    )))
  );


  setSkipEvent$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationSkipEventsStepEffectsActions.setSkipEvent),
    mergeMap(({ tool, eventType, by }) => this.depreciationService.setSkipEvent(tool, eventType).pipe(
      tap(() => {
        // if (tool.skip) {
          this.store.dispatch(DepreciationSkipEventsStepEffectsActions.getSkipEvents(
            { skip: false, eventType: tool.type.toUpperCase(), page: 1, limit: 5, filter: by.filter, search: by.search }
          ));

          this.store.dispatch(DepreciationSkipEventsStepEffectsActions.getSkipEvents(
            { skip: true, eventType: '', page: 1, limit: 5, filter: '', search: '' }
          ));

          this.store.dispatch(DepreciationFourthStepEffectsActions[
            tool.type.toUpperCase() === 'ALTA' ? 'getRegisteredToolsErrors' : 'getUnregisteredToolsErrors'
          ]());
        // } else {
        //   this.store.dispatch(DepreciationSkipEventsStepEffectsActions.getSkipEvents(
        //     { skip: true, eventType: '', page: 1, limit: 5, filter: '', search: '' }
        //   ));
        //
        //   this.store.dispatch(DepreciationSkipEventsStepEffectsActions.getSkipEvents(
        //     { skip: false, eventType: tool.type.toUpperCase(), page: 1, limit: 5, filter: by.filter, search: by.search }
        //   ));
        // }
      }),
      catchError(({ error }) => of(DepreciationSkipEventsStepEffectsActions.injectError({ error })))
    ))), { dispatch: false }
  );


  exportSkipEvents$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationSkipEventsStepEffectsActions.exportSkipEvents),
    mergeMap(({ skip, eventType }) => this.depreciationService.exportSkipEvents(skip, eventType).pipe(
      catchError(({ error }) => of(DepreciationSkipEventsStepEffectsActions.injectError({ error })))
    ))), { dispatch: false }
  );


  // Fourth step
  getRegisteredToolsErrors$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFourthStepEffectsActions.getRegisteredToolsErrors),
    mergeMap(() => this.depreciationService.getRegisteredToolsErrors().pipe(
      map(toolsErrors => DepreciationFourthStepEffectsActions.injectRegisteredToolsErrors({ toolsErrors })),
      catchError(({ error }) => of(DepreciationFourthStepEffectsActions.injectError({ error })))
    )))
  );


  getUnregisteredToolsErrors$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFourthStepEffectsActions.getUnregisteredToolsErrors),
    mergeMap(() => this.depreciationService.getUnregisteredToolsErrors().pipe(
      map(toolsErrors => DepreciationFourthStepEffectsActions.injectUnregisteredToolsErrors({ toolsErrors })),
      catchError(({ error }) => of(DepreciationFourthStepEffectsActions.injectError({ error })))
    )))
  );


  reprocessRegisteredTool$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFourthStepEffectsActions.reprocessRegisteredTool),
    mergeMap(({ id }) => this.depreciationService.reprocessRegisteredTool(id).pipe(
      map((msg) => {
        if (msg === 'ERROR') {
          return DepreciationFourthStepEffectsActions.injectError(
            { error: { errorMessage: this.errorMsg }}
          );
        } else {
          return DepreciationFourthStepEffectsActions.getRegisteredToolsErrors();
          // return DepreciationFourthStepEffectsActions.removeRegisteredTool({ id });
        }
      }),
      catchError(({ error }) => of(DepreciationFourthStepEffectsActions.injectError(
        { error: { errorMessage: this.errorMsg } }))
      )
    )))
  );

  // TO-DO: ??
  reprocessAllRegisteredTools$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFourthStepEffectsActions.reprocessAllRegisteredTools),
    mergeMap(({ ids }) => this.depreciationService.reprocessAllRegisteredTools(ids).pipe(
      map((msg) => {
        if (msg === 'ERROR') {
          console.log({ reprocessAllRegisteredTools$: msg });
          return DepreciationFourthStepEffectsActions.injectError({ error: { errorMessage: this.errorMsg }});
        } else {
          return DepreciationFourthStepEffectsActions.getRegisteredToolsErrors();
          // return DepreciationFourthStepEffectsActions.removeRegisteredTools({ ids });
        }
      }),
      catchError(({ error }) => of(DepreciationFourthStepEffectsActions.injectError(
        { error: { errorMessage: this.errorMsg }}))
      )
    )))
  );


  // reprocessAllUnregisteredTools$ = createEffect(() => this.actions$.pipe(
  //   ofType(DepreciationFourthStepEffectsActions.reprocessAllUnregisteredTools),
  //   mergeMap(({ ids }) => this.depreciationService.reprocessAllUnregisteredTools(ids).pipe(
  //     map((msg) => {
  //       // if (msg === 'ERROR') {
  //       return DepreciationFourthStepEffectsActions.injectError(
  //         { error: { errorMessage: '¡Lo sentimos! Los errores no pudieron reprocesarse. Favor reintentar.' }}
  //       );
  //       // } else {
  //       // return DepreciationFourthStepEffectsActions.removeRegisteredTools({ ids });
  //       // }
  //     }),
  //     catchError(({ error }) => of(DepreciationFourthStepEffectsActions.injectError(
  //       { error: { errorMessage: '¡Lo sentimos! Los errores no pudieron reprocesarse. Favor reintentar.' }}))
  //     )
  //   )))
  // );


  correctRegisteredTool$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFourthStepEffectsActions.correctRegisteredTool),
    mergeMap(({ id, corrections }) => this.depreciationService.correctRegisteredTool(id, corrections).pipe(
      map((msg) => {
        if (msg === 'ERROR') {
          return DepreciationFourthStepEffectsActions.injectError(
            { error: { errorMessage: this.errorMsg } }
          );
        } else {
          return DepreciationFourthStepEffectsActions.getRegisteredToolsErrors();
          // return DepreciationFourthStepEffectsActions.removeRegisteredTool({ id });
        }
      }),
      catchError(err => of(DepreciationFourthStepEffectsActions.injectError(
        { error: { errorMessage: `¡Lo sentimos! El error no pudo corregirse. ${ err?.error?.response }.` }})
      ))
    )))
  );


  correctUnregisteredTool$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFourthStepEffectsActions.correctUnregisteredTool),
    mergeMap(({ id, corrections }) => this.depreciationService.correctUnregisteredTool(id, corrections).pipe(
      map((msg) => {
        if (msg === 'ERROR') {
          return DepreciationFourthStepEffectsActions.injectError(
            { error: { errorMessage: this.errorMsg } }
          );
        } else {
          return DepreciationFourthStepEffectsActions.getUnregisteredToolsErrors();
          // return DepreciationFourthStepEffectsActions.removeUnregisteredTool({ id });
        }
      }),
      catchError(err => of(DepreciationFourthStepEffectsActions.injectError(
        { error: { errorMessage: `¡Lo sentimos! El error no pudo corregirse. ${ err?.error?.response }.` } })
      ))
    )))
  );


  reprocessUnregisteredTool$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFourthStepEffectsActions.reprocessUnregisteredTool),
    mergeMap(({ id }) => this.depreciationService.reprocessUnregisteredTool(id).pipe(
      map((msg) => {
        console.log(msg);
        return DepreciationFourthStepEffectsActions.getUnregisteredToolsErrors();
        // return DepreciationFourthStepEffectsActions.removeUnregisteredTool({ id });
      }),
      catchError(({ error }) => of(DepreciationFourthStepEffectsActions.injectError({ error })))
    )))
  );

  // Fifth Step
  downloadReportFile = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFifthStepEffectsActions.downloadReportFile),
    mergeMap(({ fileType }) => of(this.depreciationService.downloadReportFile(fileType)).pipe(
      catchError(({ error }) => of(DepreciationFifthStepEffectsActions.injectError({ error })))
    ))), { dispatch: false }
  );


  sendReport$ = createEffect(() => this.actions$.pipe(
    ofType(DepreciationFifthStepEffectsActions.sendReport),
    concatMap(() => this.depreciationService.sendReport().pipe(
      map(res => res === 'OK' ? DepreciationEffectsActions.finalizeDepreciation()
        : DepreciationFifthStepEffectsActions.injectError({ error: this.errorMsg })
      ),
      catchError(() => of(DepreciationFifthStepEffectsActions.injectError({ error: this.errorMsg })))
    )))
  );

  private disconnectSocket$!: Subject<boolean>


  constructor(
    private socketService: SocketService,
    private depreciationService: DepreciationService,
    private readonly store: Store<AppState>,
    private readonly actions$: Actions
  ) {
    this.disconnectSocket$ = new Subject();
    this.errorMsg = '¡Lo sentimos! Ocurrió un error. Favor reintentar.';
    this.errorSocket = {
      rowsWithProblems: null,
      errorMessage: `El servicio no se encuentra disponible.
      Por favor reintenta la carga nuevamente. Si el problema
      persiste, comunícate con el equipo técnico.`,
    }
  }
}
